// src/common/Documents/ActionButtons/ActionButtons.js

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import {
  Menu,
  TextField,
  Pagination,
  List,
  ListItem,
  ListItemText,
  Box,
  Divider,
  // Import CircularProgress for loading indicator
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLanguage } from "context/LanguageContext";

const ActionButtons = ({
  onRecognize,
  onRecognizeUnrecognized,
  onRecognizeHeaders,
  onDeleteSelected,
  onExportXml,
  onExportExcel,
  onExportRoda,
  onUpload,
  uploading,
  uploadProgress,
  onGeneratePdfReport, // {{ Add onGeneratePdfReport prop }}
  isLoading, // {{ Add isLoading prop for PDF generation }}
}) => {
  const { t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const exportOptions = useMemo(
    () => [
      { label: t("exportToAgnum"), value: "agnum" },
      { label: t("exportExcel"), value: "excel" },
      { label: t("exportRodaTxt") || "Export RODA TXT", value: "roda" },
    ],
    [t]
  );

  const filteredOptions = useMemo(() => {
    return exportOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [exportOptions, searchQuery]);

  const totalPages = Math.ceil(filteredOptions.length / itemsPerPage);

  const displayedOptions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOptions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOptions, currentPage, itemsPerPage]);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleOptionSelect = (value) => {
    console.log("Selected export type:", value);
    if (value === "excel") return onExportExcel();
    if (value === "roda") return onExportRoda && onExportRoda();
    onExportXml(value, exportWithMatches);
    handleClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Removed: toggle handler moved inline in menu

  const [exportWithMatches, setExportWithMatches] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        gap: "8px",
        marginBottom: "16px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <MDButton variant="outlined" color="info" size="small" onClick={onRecognize}>
        {t("recognize")}
      </MDButton>
      <MDButton variant="outlined" color="info" size="small" onClick={onRecognizeUnrecognized}>
        {t("recognizeUnrecognized")}
      </MDButton>
      <MDButton variant="outlined" color="info" size="small" onClick={onRecognizeHeaders}>
        {t("recognizeHeaders")}
      </MDButton>
      <MDButton variant="outlined" color="error" size="small" onClick={onDeleteSelected}>
        {t("deleteSelected")}
      </MDButton>

      {/* Export mygtukas su išskleidžiamuoju meniu */}
      <div>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={handleExportClick}
          endIcon={<ArrowDropDownIcon />}
        >
          {t("export")}
        </MDButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            style: { width: 300 },
          }}
        >
          {/* Paieškos laukas */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("search")}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={exportWithMatches}
                  onChange={(e) => setExportWithMatches(e.target.checked)}
                  name="exportWithMatches"
                  color="primary"
                />
              }
              label={t("exportWithMatches")}
            />
          </Box>
          <Divider />

          {/* Eksporto parinkčių sąrašas */}
          <List>
            {displayedOptions.map((option) => (
              <ListItem button key={option.value} onClick={() => handleOptionSelect(option.value)}>
                <ListItemText primary={option.label} />
              </ListItem>
            ))}
          </List>
          <Divider />

          {/* Eksporto parinkčių puslapiavimas */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Menu>
      </div>

      {/* Failų įkėlimo mygtukas */}
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        component="label"
        disabled={!!uploading}
      >
        {uploading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            {t("uploading") || "Uploading"}
            {typeof uploadProgress === "number" ? ` ${uploadProgress}%` : ""}
          </>
        ) : (
          t("uploadDocuments")
        )}
        <input type="file" multiple hidden onChange={onUpload} disabled={!!uploading} />
      </MDButton>

      {/* PDF Report Generation Button */}
      {onGeneratePdfReport && (
        <MDButton
          variant="outlined"
          color="success"
          size="small"
          onClick={onGeneratePdfReport}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              {t("generating")}
            </>
          ) : (
            t("generatePdfReport")
          )}
        </MDButton>
      )}
    </div>
  );
};

ActionButtons.propTypes = {
  onRecognize: PropTypes.func.isRequired,
  onRecognizeUnrecognized: PropTypes.func.isRequired,
  onRecognizeHeaders: PropTypes.func.isRequired,
  onDeleteSelected: PropTypes.func.isRequired,
  onExportXml: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
  onExportRoda: PropTypes.func,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onGeneratePdfReport: PropTypes.func, // {{ Add onGeneratePdfReport prop type }}
  isLoading: PropTypes.bool, // {{ Add isLoading prop type }}
};

// Set default value for isLoading
ActionButtons.defaultProps = {
  isLoading: false,
  uploading: false,
  uploadProgress: undefined,
};

export default ActionButtons;
